<template>
    <div>
        <!-- Swish Logo and Title -->
        <div class="text-center mt-5">
            <div class="d-flex justify-content-center">
                <img
                    :src="'/img/swish.svg'"
                    alt="Swish Logo"
                    class="img-fluid"
                    style="height: 3rem"
                />
            </div>
            <div class="mt-2">
                <h1 class="h5 font-weight-bold">Swish Återkommande</h1>
                <p class="text-muted">
                    Skapa medgivande för automatiska betalningar
                </p>
            </div>
        </div>

        <!-- Step Content -->
        <div v-if="['INITIATING', 'FAILED'].includes(sessionStatus)">
            <!-- Info Card -->
            <div class="alert alert-brand mb-3">
                <div class="d-flex align-items-start">
                    <p>
                        <i
                            class="fa fa-arrows-rotate mr-3 pt-1"
                            style="font-size: 1.25rem"
                        ></i>
                    </p>
                    <div>
                        <p class="font-weight-bold mb-1">
                            Vad är ett medgivande?
                        </p>
                        <p class="small mb-0">
                            Ett medgivande ger oss tillstånd att dra betalningar
                            automatiskt från ditt Swish-konto. Du godkänner en
                            gång och behöver inte signera varje betalning.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Phone Number Form -->
            <div class="card mb-3">
                <div class="card-header">
                    <h5 class="card-title d-flex align-items-center">
                        <i class="fa fa-mobile mr-2"></i>
                        Ditt telefonnummer
                    </h5>
                </div>
                <div class="card-body">
                    <form @submit.prevent="handleSubmit" class="form">
                        <div class="form-group">
                            <label
                                for="swish-recurring-phone"
                                class="small font-weight-bold text-muted"
                                >Telefonnummer kopplat till Swish</label
                            >

                            <phone-input
                                id="swish-recurring-phone"
                                name="swish-recurring-phone"
                                placeholder="0707203630"
                                v-model="phoneNumber"
                            ></phone-input>
                        </div>
                        <button
                            type="submit"
                            :disabled="!phoneNumber || submitting"
                            class="signatureComponentSignButton btn btn-secondary mb-3 btn-block"
                        >
                            <i
                                class="far fa-circle-notch fa-spin mr-1"
                                v-show="submitting"
                            ></i>
                            <template v-show="!submitting"
                                >Skapa medgivande</template
                            >
                        </button>
                        <p
                            class="small text-danger text-center p-0"
                            v-show="
                                payload?.checkoutSession?.status === 'FAILED'
                            "
                        >
                            Misslyckades med att starta en session med Swish
                        </p>
                    </form>
                </div>
            </div>
        </div>

        <div
            v-else-if="
                ['CREATED', 'SIGNING', 'PENDING'].includes(sessionStatus)
            "
        >
            <div class="card border-primary">
                <div class="card-body text-center py-5">
                    <div class="position-relative d-inline-block mb-5">
                        <div
                            class="alert-brand rounded-circle d-flex align-items-center justify-content-center mx-auto"
                            style="
                                width: 7rem;
                                height: 7rem;
                                animation: pulse 1.5s infinite;
                            "
                        >
                            <i
                                class="far fa-mobile"
                                style="font-size: 3rem"
                            ></i>
                        </div>
                    </div>
                    <div class="mb-4">
                        <h4 class="font-weight-bold">Väntar på godkännande</h4>
                        <p>
                            Öppna Swish-appen på din telefon ({{ phoneNumber }})
                            och godkänn medgivandet
                        </p>
                    </div>
                    <div
                        class="d-flex align-items-center justify-content-center text-muted"
                    >
                        <i
                            class="fa fa-circle-notch mr-1"
                            style="animation: spin 1s linear infinite"
                        ></i>
                        <span>Väntar på signering...</span>
                    </div>
                </div>
            </div>
        </div>

        <div v-else-if="sessionStatus === 'ACTIVE'">
            <div
                class="card border-success mb-3"
                style="background-color: rgba(40, 167, 69, 0.05)"
            >
                <div class="card-body text-center py-4">
                    <div
                        class="alert-success rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                        style="width: 4rem; height: 4rem"
                    >
                        <i
                            class="fal fa-check text-success"
                            style="font-size: 2rem"
                        ></i>
                    </div>
                    <div>
                        <h4 class="font-weight-semi-bold text-success mb-3">
                            Medgivande skapat!
                        </h4>
                        <p class="text-success small">
                            Ditt medgivande för återkommande betalningar är nu
                            aktivt
                        </p>
                    </div>
                </div>
            </div>

            <div class="card mb-3">
                <div class="card-header">
                    <h5 class="card-title d-flex align-items-center">
                        <i
                            class="fal fa-shield mr-2 text-success"
                            style="font-size: 1.25rem"
                        ></i>
                        Medgivandedetaljer
                    </h5>
                </div>
                <div class="card-body small">
                    <div
                        class="d-flex justify-content-between align-items-center mb-2"
                    >
                        <span class="text-muted">Medgivande-ID</span>
                        <span
                            class="font-monospace bg-light px-2 py-1 rounded small"
                            >{{ payload?.checkoutSession?.consentId }}</span
                        >
                    </div>
                    <div
                        class="d-flex justify-content-between align-items-center mb-2"
                    >
                        <span class="text-muted">Telefonnummer</span>
                        <span class="font-weight-bold">{{ phoneNumber }}</span>
                    </div>
                    <div
                        class="d-flex justify-content-between align-items-center mb-2"
                    >
                        <span class="text-muted">Status</span>
                        <span
                            class="text-success font-weight-bold d-flex align-items-center"
                        >
                            <i
                                class="bi bi-check-circle mr-1"
                                style="font-size: 0.75rem"
                            ></i>
                            Aktivt
                        </span>
                    </div>
                    <div
                        class="d-flex justify-content-between align-items-center"
                    >
                        <span class="text-muted">Giltig till</span>
                        <span class="font-weight-bold">Tills vidare</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from "vue";
import axios from "axios";
import { route } from "ziggy";

const props = defineProps({
    initialPhoneNumber: { default: undefined, required: false },
    payload: { required: true },
    modelValue: { default: undefined },
});
const emit = defineEmits([
    "update:modelValue",
    "submitted",
    "success",
    "failed",
]);

const phoneNumber = ref(null);
const checkoutInterval = ref(null);
const checkoutTimeout = ref(null);
const submitting = ref(false);
const sessionStatus = ref("INITIATING");

function stopCheckoutTimers() {
    clearInterval(checkoutInterval.value);
    clearTimeout(checkoutTimeout.value);
    checkoutInterval.value = null;
    checkoutTimeout.value = null;
}

function resetCheckout() {
    submitting.value = false;
    stopCheckoutTimers();
    sessionStatus.value = "INITIATING";
}

function handleSubmit() {
    submitting.value = false;
    emit("update:modelValue", phoneNumber.value);
    emit("submitted", phoneNumber.value);
    submitting.value = true;
}

watch(
    () => props.payload,
    async (value, oldValue) => {
        if (
            oldValue?.checkoutSession?.status === "INITIATING" &&
            oldValue?.checkoutSession?.status === sessionStatus.value &&
            value?.checkoutSession?.status === "CREATED"
        ) {
            submitting.value = false;
            sessionStatus.value = value.checkoutSession.status;
        }

        if (value?.checkoutSession?.status === "FAILED") {
            resetCheckout();
            return;
        }

        stopCheckoutTimers();

        if (
            !value ||
            value.checkoutSession.type !== "swish-recurring" ||
            !value.paymentRequestId
        ) {
            return;
        }

        checkoutInterval.value = setInterval(() => {
            axios
                .get(
                    route("quote.products.payments.get", [
                        value.quoteKey,
                        value.productId,
                        value.paymentRequestId,
                    ])
                )
                .then((response) => {
                    sessionStatus.value =
                        response.data.checkout_session.status;
                    if (sessionStatus.value === "ACTIVE") {
                        stopCheckoutTimers();
                        emit("success", response.data);
                    } else if (
                        ["DECLINED", "TIMEOUT"].includes(sessionStatus.value)
                    ) {
                        emit("failed", response.data);
                        resetCheckout();
                    }
                })
                .catch((error) => {
                    console.warn(
                        "Failed to check Swish recurring status.",
                        error
                    );
                });
        }, 1000);

        checkoutTimeout.value = setTimeout(() => {
            if (checkoutInterval.value) {
                resetCheckout();
            }
        }, 180 * 1000);
    }
);

onBeforeUnmount(stopCheckoutTimers);
</script>

<style scoped>
/* Custom animations for Bootstrap (since Bootstrap doesn't have built-in pulse/bounce/spin) */
@keyframes pulse {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.05);
    }
    100% {
        transform: scale(1);
    }
}
@keyframes bounce {
    0%,
    100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-5px);
    }
}
@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
/* Hover shadow for button */
.hover-shadow-lg:hover {
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}
.transition-all {
    transition: all 0.3s;
}
</style>
